import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Calendar,
  CheckCircle2,
  Info,
  Plus,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import api from "../../../services/api";
import { useToast } from "../../../contexts/toast/toast-context";
import { useAuth } from "../../../hooks/use-auth";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { LoadingButton } from "../../../components/button/loading-button";
import { useDebounce } from "../../../hooks/use-debounce";
import {
  formatHour,
  formatUserPhone,
  getApiErrorMessage,
  normalizePhone,
} from "../../../utils";
import {
  formatDateKey,
  isDateSelectableForTenant,
} from "../../../utils/appointment";
import type {
  Appointment,
  AppointmentDayCapacity,
  AppointmentStatus,
} from "../../../types/appointment";
import type { Tenant } from "../../../types/tenant";
import type { User } from "../../../types/user";

type FindAllAppointmentDto = {
  data: Appointment[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const STATUS_META: Record<
  AppointmentStatus,
  { label: string; summary: string; dot: string; text: string }
> = {
  SCHEDULED: {
    label: "Agendado",
    summary: "Agendados",
    dot: "bg-amber-500",
    text: "text-amber-600",
  },
  CANCELED: {
    label: "Cancelado",
    summary: "Cancelados",
    dot: "bg-rose-500",
    text: "text-rose-600",
  },
};

const CONFIRMED_META = {
  label: "Confirmado",
  summary: "Confirmados",
  dot: "bg-emerald-500",
  text: "text-emerald-600",
} as const;

export default function AppointmentPage() {
  const { user } = useAuth();
  const { error, success, info } = useToast();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSchedulingEnabled, setIsSchedulingEnabled] = useState<
    boolean | null
  >(null);
  const [isTenantLoading, setIsTenantLoading] = useState(false);
  const [tenantConfig, setTenantConfig] = useState<Tenant | null>(null);
  const [dayCapacities, setDayCapacities] = useState<
    AppointmentDayCapacity[]
  >([]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebounce(userSearch, 400);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState<{
    id: string;
    action: "confirm" | "cancel";
  } | null>(null);

  const [appointmentToDelete, setAppointmentToDelete] =
    useState<Appointment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!user?.appName) return;

    const fetchTenant = async () => {
      setIsTenantLoading(true);
      try {
        const response = await api.get<Tenant>(`/tenant/${user.appName}`);
        setIsSchedulingEnabled(response.data.enabledScheduling);
        setTenantConfig(response.data);
      } catch (err) {
        error(getApiErrorMessage(err));
        setIsSchedulingEnabled(false);
        setTenantConfig(null);
      } finally {
        setIsTenantLoading(false);
      }
    };

    fetchTenant();
  }, [user?.appName, error]);

  const fetchAppointments = useCallback(async () => {
    if (!isSchedulingEnabled) return;
    const startDate = new Date(selectedDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(selectedDate);
    endDate.setHours(23, 59, 59, 999);

    setIsLoading(true);

    try {
      const response = await api.get<FindAllAppointmentDto>("/scheduling", {
        params: {
          page: 1,
          limit: 200,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });

      setAppointments(response.data.data);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, error, isSchedulingEnabled]);

  const fetchAvailableTimes = useCallback(async () => {
    if (!isSchedulingEnabled) return;
    try {
      const response = await api.get<string[]>("/scheduling/available-times", {
        params: {
          date: formatDateKey(selectedDate),
        },
      });

      setAvailableTimes(response.data);
    } catch (err) {
      error(getApiErrorMessage(err));
    }
  }, [selectedDate, error, isSchedulingEnabled]);

  const fetchDayCapacities = useCallback(async () => {
    if (!isSchedulingEnabled) return;

    try {
      const response = await api.get<AppointmentDayCapacity[]>(
        "/scheduling/day-capacity",
        {
          params: {
            date: formatDateKey(selectedDate),
          },
        },
      );

      setDayCapacities(response.data);
    } catch (err) {
      error(getApiErrorMessage(err));
      setDayCapacities([]);
    }
  }, [selectedDate, error, isSchedulingEnabled]);

  useEffect(() => {
    if (!isSchedulingEnabled) return;
    fetchAppointments();
  }, [fetchAppointments, isSchedulingEnabled]);

  useEffect(() => {
    if (!isSchedulingEnabled) return;
    fetchDayCapacities();
  }, [fetchDayCapacities, isSchedulingEnabled]);

  useEffect(() => {
    if (!isCreateOpen) return;
    fetchAvailableTimes();
  }, [fetchAvailableTimes, isCreateOpen]);

  useEffect(() => {
    if (!isCreateOpen) return;
    setSelectedTime("");
  }, [selectedDate, isCreateOpen]);

  useEffect(() => {
    if (!isCreateOpen) return;

    const trimmedSearch = debouncedUserSearch.trim();

    if (!trimmedSearch) {
      setUserOptions([]);
      return;
    }

    const fetchUsers = async () => {
      try {
        const response = await api.get<{ data: User[] }>("/user", {
          params: {
            page: 1,
            limit: 10,
            search: trimmedSearch,
          },
        });

        setUserOptions(response.data.data);
      } catch (err) {
        error(getApiErrorMessage(err));
      }
    };

    fetchUsers();
  }, [debouncedUserSearch, isCreateOpen, error]);

  const filteredAppointments = useMemo(() => {
    const rawSearch = search.trim().toLowerCase();
    const phoneSearch = normalizePhone(rawSearch);
    const sorted = [...appointments].sort(
      (a, b) =>
        new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
    );

    if (!rawSearch) return sorted;

    return sorted.filter((appointment) => {
      const name = appointment.user.userName?.toLowerCase() ?? "";
      const phone = normalizePhone(appointment.user.userPhone ?? "");
      return (
        name.includes(rawSearch) ||
        (phoneSearch.length > 0 && phone.includes(phoneSearch))
      );
    });
  }, [appointments, search]);

  const summary = useMemo(() => {
    const counts = {
      total: appointments.length,
      SCHEDULED: 0,
      CANCELED: 0,
      confirmed: 0,
    };

    appointments.forEach((appointment) => {
      counts[appointment.status] += 1;
      if (appointment.confirmed) {
        counts.confirmed += 1;
      }
    });

    return counts;
  }, [appointments]);

  const dayCapacityMap = useMemo(() => {
    return dayCapacities.reduce<Record<string, AppointmentDayCapacity>>(
      (acc, item) => {
        acc[item.date] = item;
        return acc;
      },
      {},
    );
  }, [dayCapacities]);

  const selectedDateLabel = useMemo(
    () =>
      selectedDate.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    [selectedDate],
  );

  const openGoogleCalendar = () => {
    const url = `https://calendar.google.com/calendar/u/0/r?authuser=${user?.tenantEmail}`;
    window.open(url, "_blank");
  };

  const resetCreateForm = () => {
    setSelectedUserId("");
    setSelectedTime("");
    setAdditionalInfo("");
    setUserSearch("");
    setUserOptions([]);
  };

  const isDateSelectable = useCallback(
    (date: Date) => isDateSelectableForTenant(date, tenantConfig),
    [tenantConfig],
  );

  const getCalendarDayClassName = useCallback(
    (date: Date) => {
      const capacity = dayCapacityMap[formatDateKey(date)];
      if (!capacity || capacity.level === "unavailable") return "";

      return `appointment-capacity-day appointment-capacity-${capacity.level}`;
    },
    [dayCapacityMap],
  );

  const handleCreateAppointment = async () => {
    if (!selectedUserId) {
      info("Selecione um contato para agendar.");
      return;
    }

    if (!selectedTime) {
      info("Selecione um horário disponível.");
      return;
    }

    const [hours, minutes] = selectedTime.split(":").map(Number);
    const scheduledAt = new Date(selectedDate);
    scheduledAt.setHours(hours, minutes, 0, 0);

    setIsSaving(true);

    try {
      await api.post("/scheduling", {
        userId: selectedUserId,
        scheduledAt: scheduledAt.toISOString(),
        additionalInfo: additionalInfo.trim() || undefined,
      });

      success("Agendamento criado com sucesso!");
      setIsCreateOpen(false);
      resetCreateForm();
      fetchAppointments();
      fetchAvailableTimes();
      fetchDayCapacities();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!appointmentToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/scheduling/${appointmentToDelete.id}`);
      setAppointments((prev) =>
        prev.filter((s) => s.id !== appointmentToDelete.id),
      );
      setAppointmentToDelete(null);
      success("Agendamento removido com sucesso!");
      fetchDayCapacities();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirm = async (appointmentId: string) => {
    setStatusUpdating({ id: appointmentId, action: "confirm" });
    try {
      const response = await api.patch<Appointment>(
        `/scheduling/${appointmentId}/confirm`,
      );

      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId ? response.data : appointment,
        ),
      );
      success("Status atualizado com sucesso!");
      fetchDayCapacities();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleCancel = async (appointmentId: string) => {
    setStatusUpdating({ id: appointmentId, action: "cancel" });
    try {
      const response = await api.patch<Appointment>(
        `/scheduling/${appointmentId}/cancel`,
      );

      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId ? response.data : appointment,
        ),
      );
      success("Status atualizado com sucesso!");
      fetchDayCapacities();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setStatusUpdating(null);
    }
  };

  if (isTenantLoading || isSchedulingEnabled === null) {
    return (
      <div className="app-page">
        <div className="app-card p-6 text-sm text-slate-500">
          Carregando configurações do agendamento...
        </div>
      </div>
    );
  }

  if (!isSchedulingEnabled) {
    return (
      <div className="app-page">
        <div className="app-card space-y-3 p-6">
          <h1 className="text-xl font-semibold text-slate-800">
            Agendamentos desativados
          </h1>
          <p className="text-sm text-slate-500">
            O módulo de agendamentos está desabilitado.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page">
      <div className="flex flex-col gap-2 mb-6">
        <h1 className="app-page-title">Pacientes do dia</h1>
        <p className="text-sm text-slate-500">
          Controle diário e criação de novos agendamentos para seus contatos.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
        <section className="app-card flex flex-col p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-slate-800 font-semibold">
              <Calendar className="w-5 h-5 text-slate-500" />
              <span>
                Agenda do dia{" "}
                {selectedDate.toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="form-input w-full py-2 pl-9 pr-3"
                type="text"
                placeholder="Buscar por nome ou telefone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {isLoading && (
              <div className="text-sm text-slate-500">Carregando agenda...</div>
            )}

            {!isLoading && filteredAppointments.length === 0 && (
              <div className="app-empty-state text-center">
                Nenhum agendamento encontrado para esta data.
              </div>
            )}

            {filteredAppointments.map((appointment) => {
              const meta =
                appointment.status === "CANCELED"
                  ? STATUS_META.CANCELED
                  : appointment.confirmed
                    ? CONFIRMED_META
                    : STATUS_META.SCHEDULED;
              const isUpdating = statusUpdating?.id === appointment.id;
              const isConfirming =
                isUpdating && statusUpdating?.action === "confirm";
              const isCanceling =
                isUpdating && statusUpdating?.action === "cancel";
              const trimmedAdditionalInfo = appointment.additionalInfo?.trim();
              const confirmedAt = appointment.confirmedAt
                ? new Date(appointment.confirmedAt)
                : null;
              const hasValidConfirmedAt =
                confirmedAt && !Number.isNaN(confirmedAt.getTime());
              const confirmedAtDate = hasValidConfirmedAt
                ? (confirmedAt as Date)
                : null;
              return (
                <div
                  key={appointment.id}
                  className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={`h-3 w-3 rounded-full ${meta.dot}`}
                      aria-hidden
                    />
                    <div>
                      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800">
                        <span>
                          {formatHour(new Date(appointment.scheduledAt))}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span>{appointment.user.userName}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>{meta.label}</span>
                        <span className="text-slate-300">|</span>
                        <span>
                          {appointment.agentType === "AI"
                            ? "Agendado por IA"
                            : "Agendado pelo atendente"}
                        </span>
                        {confirmedAtDate && (
                          <>
                            <span className="text-slate-300">|</span>
                            <span>
                              Confirmado em {formatHour(confirmedAtDate)} (
                              {confirmedAtDate.toLocaleDateString("pt-BR")})
                            </span>
                          </>
                        )}
                        <span className="text-slate-300">|</span>
                        <span>{formatUserPhone(appointment.user)}</span>
                        {trimmedAdditionalInfo && (
                          <>
                            <span className="text-slate-300">|</span>
                            <span
                              title={trimmedAdditionalInfo}
                              aria-label={`Informações adicionais: ${trimmedAdditionalInfo}`}
                              className="inline-flex items-center text-slate-400 cursor-help"
                            >
                              <Info className="w-3.5 h-3.5" />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <LoadingButton
                      onClick={() => handleConfirm(appointment.id)}
                      className="btn-link-success flex items-center gap-1 transition disabled:opacity-60"
                      title="Confirmar agendamento"
                      disabled={
                        isUpdating ||
                        appointment.status !== "SCHEDULED" ||
                        appointment.confirmed
                      }
                      isLoading={isConfirming}
                      loadingLabel="Confirmando"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-xs font-medium">Confirmar</span>
                    </LoadingButton>
                    <LoadingButton
                      onClick={() => handleCancel(appointment.id)}
                      className="btn-link-danger flex items-center gap-1 transition disabled:opacity-60"
                      title="Cancelar agendamento"
                      disabled={isUpdating || appointment.status === "CANCELED"}
                      isLoading={isCanceling}
                      loadingLabel="Cancelando"
                    >
                      <XCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Cancelar</span>
                    </LoadingButton>
                    <button
                      onClick={() => setAppointmentToDelete(appointment)}
                      className="btn-link-danger transition"
                      title="Excluir agendamento"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="flex flex-col gap-5">
          <button
            onClick={() => setIsCreateOpen(true)}
            className="btn btn-neutral rounded-xl px-4 py-3"
          >
            <Plus className="w-4 h-4" />
            Novo agendamento
          </button>

          <div className="app-card p-4">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              inline
              calendarStartDay={1}
              filterDate={isDateSelectable}
              dayClassName={getCalendarDayClassName}
            />
          </div>

          <button
            onClick={openGoogleCalendar}
            className="btn btn-secondary w-full rounded-xl px-4 py-3"
          >
            <Calendar className="w-4 h-4" />
            Abrir Google Calendar
          </button>

          <div className="app-card p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">
              Resumo do dia
            </h3>

            <div className="space-y-3 text-sm text-slate-600">
              <div className="flex items-center justify-between">
                <span>Total de agendamentos</span>
                <span className="font-semibold text-slate-800">
                  {summary.total}
                </span>
              </div>

              {(Object.keys(STATUS_META) as AppointmentStatus[]).map(
                (status) => {
                  const meta = STATUS_META[status];
                  return (
                    <div
                      key={status}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${meta.dot}`}
                        />
                        <span>{meta.summary}</span>
                      </div>
                      <span className={`font-semibold ${meta.text}`}>
                        {summary[status]}
                      </span>
                    </div>
                  );
                },
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${CONFIRMED_META.dot}`}
                  />
                  <span>{CONFIRMED_META.summary}</span>
                </div>
                <span className={`font-semibold ${CONFIRMED_META.text}`}>
                  {summary.confirmed}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {isCreateOpen && (
        <div
          className="modal-overlay"
          onClick={() => {
            setIsCreateOpen(false);
            resetCreateForm();
          }}
        >
          <div
            className="modal-card-lg space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Novo agendamento
              </h2>
              <p className="text-sm text-slate-500">
                Selecione o contato e o horário disponível.
              </p>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase text-slate-500">
                  <Calendar className="h-4 w-4" />
                  <span>Dia selecionado</span>
                </div>
                <p className="mt-1 text-sm font-semibold capitalize text-slate-800">
                  {selectedDateLabel}
                </p>
              </div>

              <div>
                <label className="form-label">Contato</label>
                <input
                  type="text"
                  placeholder="Buscar contato por nome ou telefone"
                  className="form-input mt-2"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <select
                  className="form-input mt-2"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">
                    {userSearch.trim()
                      ? "Selecione um contato"
                      : "Digite para buscar"}
                  </option>
                  {userOptions.length === 0 && userSearch.trim() && (
                    <option value="" disabled>
                      Nenhum contato encontrado
                    </option>
                  )}
                  {userOptions.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.userName} • {formatUserPhone(client)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Horário disponível</label>
                <select
                  className="form-input mt-2"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                >
                  <option value="">
                    {availableTimes.length
                      ? "Selecione um horário"
                      : "Nenhum horário disponível"}
                  </option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Informações adicionais</label>
                <textarea
                  className="form-textarea mt-2"
                  rows={3}
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder="Ex.: preferência de atendimento, observações clínicas, recados."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setIsCreateOpen(false);
                  resetCreateForm();
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <LoadingButton
                onClick={handleCreateAppointment}
                className="btn btn-neutral"
                isLoading={isSaving}
                loadingLabel="Salvando"
              >
                Criar agendamento
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={!!appointmentToDelete}
        title="Confirmar exclusão"
        description="Tem certeza que deseja excluir este agendamento?"
        onCancel={() => setAppointmentToDelete(null)}
        onConfirm={handleDeleteAppointment}
        isLoading={isDeleting}
      />
    </div>
  );
}
